import React, { useEffect, useRef, useState } from "react";
import {
  ArrowRight,
  Check,
  ChevronDown,
  FileArchive,
  HardDrive,
  Loader2,
  WifiOff,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { dateLocale } from "i18n";
import betaAPI from "v2/api";
import { useAuth } from "hooks/useAuth";
import { parseDfuPackage } from "utils/nordicDfu";
import { operatorNotes } from "utils/firmwareRelease";
import {
  FIRMWARE_CHANNELS,
  fromApi,
  fromLocal,
  isOfflineError,
  mergeReleases,
  newestSaved,
  releaseLabel,
  releaseZipName,
} from "utils/firmwareSource";

// Packages from before release.json existed: "Sports_impact_FW_v0.6.41_…zip"
// → "v0.6.41". (The init packet's own version is always 1, so no help.)
const versionFromFileName = (name) => {
  const m = /v(\d+\.\d+\.\d+)/i.exec(name || "");
  return m ? `v${m[1]}` : null;
};

export const formatKb = (bytes) => `${Math.round(bytes / 1024)} KB`;

/** Zip bytes as an update package; throws a DfuError if it isn't one. */
export const readFirmwareBytes = (bytes, name) => {
  const parsed = parseDfuPackage(bytes);
  return {
    ...parsed,
    name,
    size: bytes.byteLength ?? bytes.length,
    version: parsed.release?.version || versionFromFileName(name),
  };
};

/** A chosen .zip as an update package; throws a DfuError if it isn't one. */
export const readFirmwarePackage = async (file) => {
  const pkg = readFirmwareBytes(await file.arrayBuffer(), file.name);
  return {
    ...pkg,
    size: file.size,
    source: "file",
    label: pkg.version || file.name,
  };
};

const packageErrorText = (t, e) => {
  if (e?.code === "package") {
    return e.multiImage
      ? t("deviceTools.updateFirmware.errors.multiImage")
      : t("deviceTools.updateFirmware.errors.package");
  }
  return e?.message || String(e);
};

const fmtDate = (iso) =>
  iso
    ? new Date(iso).toLocaleDateString(dateLocale("en-GB"), {
        day: "numeric",
        month: "short",
        year: "numeric",
      })
    : null;

const CHANNEL_LABELS = ["production", "dev", "test", "local"];

const btnSmall =
  "flex flex-shrink-0 items-center gap-1.5 rounded-lg border border-[#E5E7EB] bg-white px-3.5 py-2 text-[14px] font-medium text-[#374151] transition duration-150 hover:bg-[#F9FAFB] disabled:opacity-50";
const btnText =
  "flex items-center gap-1.5 text-[14px] font-medium text-[#6B7280] transition duration-150 hover:text-[#111827]";

/** Production is the normal case and gets no badge; anything else is flagged. */
const ChannelPill = ({ channel }) => {
  const { t } = useTranslation();
  if (!CHANNEL_LABELS.includes(channel) || channel === "production") {
    return null;
  }
  return (
    <span className="flex-shrink-0 rounded-md bg-[#FEF3E7] px-2 py-0.5 text-[12px] font-medium text-[#B45309]">
      {t(`deviceTools.updateFirmware.channels.${channel}`)}
    </span>
  );
};

/**
 * The firmware about to go on: "Installed v0.6.16 → New version v1.2.0" when
 * the device's version is known, else just the version, with one "Change".
 */
const VersionSummary = ({
  currentVersion,
  label,
  channel,
  dirty,
  meta,
  busy,
  onChange,
}) => {
  const { t } = useTranslation();
  return (
    <div>
      <div className="flex items-end gap-4">
        {currentVersion && (
          <>
            <div className="min-w-0">
              <p className="text-[14px] font-medium text-[#6B7280]">
                {t("deviceTools.firmwareSource.installed")}
              </p>
              <p className="mt-1 truncate text-[28px] font-semibold tabular-nums leading-tight text-[#9CA3AF]">
                {currentVersion}
              </p>
            </div>
            <ArrowRight
              size={18}
              className="mb-2.5 flex-shrink-0 text-[#D1D5DB]"
            />
          </>
        )}
        <div className="min-w-0 flex-1">
          <p className="text-[14px] font-medium text-[#6B7280]">
            {currentVersion
              ? t("deviceTools.firmwareSource.newVersion")
              : t("deviceTools.firmwareSource.firmware")}
          </p>
          <p className="mt-1 flex min-w-0 items-center gap-2.5">
            <span className="truncate text-[28px] font-semibold tabular-nums leading-tight text-[#111827]">
              {label}
            </span>
            <ChannelPill channel={channel} />
            {dirty && (
              <span className="flex-shrink-0 rounded-md bg-[#FEF3E7] px-2 py-0.5 text-[12px] font-medium text-[#B45309]">
                {t("deviceTools.updateFirmware.uncommitted")}
              </span>
            )}
          </p>
        </div>
        <button onClick={onChange} disabled={busy} className={btnSmall}>
          {t("deviceTools.updateFirmware.change")}
        </button>
      </div>
      {(meta || busy) && (
        <p className="mt-2.5 flex items-center gap-1.5 text-[14px] text-[#6B7280]">
          {busy && (
            <Loader2 size={13} className="animate-spin text-[#E07A3A]" />
          )}
          {busy ? t("deviceTools.firmwareSource.downloading") : meta}
        </p>
      )}
    </div>
  );
};

const NOTES_PREVIEW = 3;

/**
 * "What's new" for operators: the release's hand-written highlights up
 * front, the device-facing PRs behind "Show all changes". Without highlights
 * the first few changes stand in for them.
 */
export const FirmwareNotes = ({ notes, version, className = "" }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const { highlights, changes } = operatorNotes(notes);
  if (!highlights.length && !changes.length) return null;
  const lead = highlights.length ? highlights : changes.slice(0, NOTES_PREVIEW);
  const rest = highlights.length ? changes : changes.slice(NOTES_PREVIEW);
  return (
    <div className={className}>
      <p className="text-[16px] font-semibold text-[#111827]">
        {version
          ? t("deviceTools.firmwareSource.whatsNewIn", { version })
          : t("deviceTools.updateFirmware.whatsNew")}
      </p>
      <NoteList items={lead} strong={highlights.length > 0} />
      {rest.length > 0 && (
        <>
          <button
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            className={`${btnText} mt-3`}
          >
            <ChevronDown
              size={14}
              className={`transition-transform duration-150 ${
                open ? "rotate-180" : ""
              }`}
            />
            {open
              ? t("deviceTools.firmwareSource.hideChanges")
              : highlights.length
              ? t("deviceTools.firmwareSource.allChanges", {
                  count: rest.length,
                })
              : t("deviceTools.firmwareSource.moreChanges", {
                  count: rest.length,
                })}
          </button>
          {open && <NoteList items={rest} />}
        </>
      )}
    </div>
  );
};

const NoteList = ({ items, strong = false }) => (
  <ul className="mt-3 space-y-2.5">
    {items.map((item, i) => (
      <li
        key={i}
        className={`flex gap-2.5 leading-snug ${
          strong ? "text-[16px] text-[#111827]" : "text-[15px] text-[#374151]"
        }`}
      >
        <span
          className={`mt-[10px] h-1.5 w-1.5 flex-shrink-0 rounded-full ${
            strong ? "bg-[#E07A3A]" : "bg-[#D1D5DB]"
          }`}
        />
        <span className="min-w-0">
          {item.text}
          {item.pr && (
            <span className="ml-1.5 text-[12px] tabular-nums text-[#9CA3AF]">
              #{item.pr}
            </span>
          )}
        </span>
      </li>
    ))}
  </ul>
);

/** One release in the chooser: version, "Latest", date, saved/loading. */
const ReleaseRow = ({
  release,
  latest,
  selected,
  loading,
  disabled,
  onPick,
}) => {
  const { t } = useTranslation();
  return (
    <li>
      <button
        onClick={onPick}
        disabled={disabled}
        aria-pressed={selected}
        className={`flex w-full items-center gap-3 px-3 py-3 text-left transition duration-150 disabled:cursor-default ${
          selected ? "bg-[#FEF7F3]" : "hover:bg-[#FAFAFA]"
        }`}
      >
        <span
          className={`flex h-[18px] w-[18px] flex-shrink-0 items-center justify-center rounded-full border ${
            selected ? "border-[#E07A3A] bg-[#E07A3A]" : "border-[#D1D5DB]"
          }`}
        >
          {selected && <Check size={11} className="text-white" />}
        </span>
        <span className="min-w-0 flex-1 truncate text-[16px] font-medium tabular-nums text-[#111827]">
          {releaseLabel(release)}
        </span>
        {latest && (
          <span className="flex-shrink-0 text-[12px] font-medium text-[#15803D]">
            {t("deviceTools.firmwareSource.latest")}
          </span>
        )}
        <span className="w-28 flex-shrink-0 text-right text-[14px] tabular-nums text-[#6B7280]">
          {fmtDate(release.releasedAt)}
        </span>
        <span className="flex w-4 flex-shrink-0 justify-center">
          {loading ? (
            <Loader2 size={14} className="animate-spin text-[#E07A3A]" />
          ) : (
            release.saved && (
              <HardDrive
                size={14}
                className="text-[#9CA3AF]"
                aria-label={t("deviceTools.firmwareSource.savedTitle")}
              >
                <title>{t("deviceTools.firmwareSource.savedTitle")}</title>
              </HardDrive>
            )
          )}
        </span>
      </button>
    </li>
  );
};

/** File-only picker (non-superadmins, or the browser). */
const FilePicker = ({ pkg, currentVersion, onOpen }) => {
  const { t } = useTranslation();
  if (!pkg) {
    return (
      <button
        onClick={onOpen}
        className="flex w-full items-center justify-center gap-2 rounded-xl border border-dashed border-[#D1D5DB] px-4 py-6 text-[14px] font-medium text-[#374151] transition duration-150 hover:border-[#E07A3A] hover:bg-[#FEF7F3]"
      >
        <FileArchive size={16} className="text-[#9CA3AF]" />
        {t("deviceTools.updateFirmware.choosePackage")}
      </button>
    );
  }
  const builtOn = fmtDate(pkg.release?.builtAt);
  return (
    <VersionSummary
      currentVersion={currentVersion}
      label={pkg.label || pkg.version || pkg.name}
      channel={pkg.release?.channel}
      dirty={pkg.release?.dirty}
      meta={[
        pkg.name,
        builtOn && t("deviceTools.updateFirmware.builtOn", { date: builtOn }),
        formatKb(pkg.firmware.length),
      ]
        .filter(Boolean)
        .join(" · ")}
      onChange={onOpen}
    />
  );
};

/**
 * Which firmware goes on, and what's new in it. Superadmins in the desktop
 * app get the published releases: the newest production firmware is picked
 * (and saved for offline use) as soon as the screen opens, and "Change"
 * lists every production release and dev build, or takes a zip from disk.
 * Anyone else uses a zip from disk.
 *
 * `onPackage(pkg)` gets the parsed package (pkg.source: "release" | "file",
 * pkg.sourceTag for a release, pkg.label for display); `onError(message)` a
 * failure to load one. `currentVersion` (the device's) shows "from → to".
 */
const FirmwareSource = ({
  pkg,
  onPackage,
  onError,
  currentVersion,
  showNotes = true,
  className = "",
}) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const firmwareApi = window.electronAPI?.firmware;
  const enabled = !!firmwareApi && user?.is_superadmin === true;

  const [saved, setSaved] = useState([]); // computer's index rows
  const [latest, setLatest] = useState(null); // { production, dev } once loaded
  const [offline, setOffline] = useState(false);
  const [loading, setLoading] = useState(null); // release being loaded
  const [chosen, setChosen] = useState(null); // release behind `pkg`
  const [choosing, setChoosing] = useState(false);
  const [tab, setTab] = useState("production");
  const [lists, setLists] = useState({}); // channel -> API rows | null
  const fileInputRef = useRef(null);
  const aliveRef = useRef(true);
  // Set on (re)mount too: StrictMode unmounts and remounts once in development.
  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
    };
  }, []);
  const pkgRef = useRef(pkg);
  pkgRef.current = pkg;

  const listSaved = async () => {
    const res = await firmwareApi.list();
    const rows = res?.ok ? res.releases : [];
    if (aliveRef.current) setSaved(rows);
    return rows;
  };

  const loadRelease = async (release) => {
    if (loading) return;
    setLoading(release);
    try {
      const rows = saved.length ? saved : await listSaved();
      const copy = rows.find(
        (r) =>
          r.tag === release.tag &&
          (!release.sha256 || r.sha256 === release.sha256)
      );
      if (!copy) {
        // A fresh link every time: presigned URLs expire after an hour.
        const { data } = await betaAPI
          .getWearableFirmwareVersion({
            tag: release.tag,
            channel: release.channel,
          })
          .catch((e) => {
            throw Object.assign(e, { offline: isOfflineError(e) });
          });
        const res = await firmwareApi.download(data);
        if (!res.ok) throw new Error(res.error?.message);
      }
      const res = await firmwareApi.read(release.tag);
      if (!res.ok) throw new Error(res.error?.message);
      const loaded = readFirmwareBytes(res.bytes, releaseZipName(release.tag));
      if (!aliveRef.current) return;
      setChosen(release);
      setChoosing(false);
      onPackage({
        ...loaded,
        source: "release",
        sourceTag: release.tag,
        label: releaseLabel(release),
      });
      listSaved();
    } catch (e) {
      console.error("Failed to load firmware release", e);
      if (!aliveRef.current) return;
      onError(
        e?.offline
          ? t("deviceTools.firmwareSource.downloadOffline")
          : t("deviceTools.firmwareSource.loadFailed", {
              version: releaseLabel(release),
              reason: packageErrorText(t, e),
            })
      );
    } finally {
      if (aliveRef.current) setLoading(null);
    }
  };

  // The newest release on each channel, from the API or (offline) from what's
  // saved here; then pick the newest production one unless a package is set.
  useEffect(() => {
    if (!enabled) return;
    (async () => {
      const rows = await listSaved();
      const results = await Promise.allSettled(
        FIRMWARE_CHANNELS.map((channel) =>
          betaAPI.getWearableFirmwareLatest({ channel })
        )
      );
      if (!aliveRef.current) return;
      const next = {};
      let anyOffline = false;
      FIRMWARE_CHANNELS.forEach((channel, i) => {
        const r = results[i];
        if (r.status === "fulfilled") {
          next[channel] = fromApi(r.value.data);
        } else {
          if (isOfflineError(r.reason)) anyOffline = true;
          // Offline (or nothing published yet): the newest copy saved here.
          next[channel] = newestSaved(rows, channel);
        }
      });
      setOffline(anyOffline);
      setLatest(next);
      const first = next.production || next.dev;
      if (first && !pkgRef.current) loadRelease(first);
    })();
    // eslint-disable-next-line
  }, [enabled]);

  // The list shows on "Change", or by itself when nothing could be loaded.
  const listOpen = choosing || (!pkg && !!latest && !loading);
  useEffect(() => {
    if (!enabled || !listOpen || lists[tab] !== undefined) return;
    setLists((p) => ({ ...p, [tab]: null }));
    betaAPI
      .getWearableFirmwareReleases({ channel: tab })
      .then(({ data }) =>
        setLists((p) => ({ ...p, [tab]: data?.releases || [] }))
      )
      .catch((e) => {
        console.error("Failed to list firmware releases", e);
        if (isOfflineError(e)) setOffline(true);
        // Fall back to what's saved on this computer.
        setLists((p) => ({ ...p, [tab]: [] }));
      });
    // eslint-disable-next-line
  }, [enabled, listOpen, tab]);

  const chooseFile = async (file) => {
    if (!file) return;
    try {
      const next = await readFirmwarePackage(file);
      setChosen(null);
      setChoosing(false);
      onPackage(next);
    } catch (e) {
      onError(packageErrorText(t, e));
    }
  };
  const openFile = () => fileInputRef.current?.click();

  const fileInput = (
    <input
      ref={fileInputRef}
      type="file"
      accept=".zip"
      className="hidden"
      onChange={(e) => {
        chooseFile(e.target.files?.[0]);
        e.target.value = "";
      }}
    />
  );
  const notes = showNotes && !choosing && pkg?.release?.notes && (
    <FirmwareNotes
      notes={pkg.release.notes}
      version={pkg.label}
      className="mt-5 border-t border-[#F3F4F6] pt-5"
    />
  );

  if (!enabled) {
    return (
      <div className={className}>
        {fileInput}
        <FilePicker
          pkg={pkg}
          currentVersion={currentVersion}
          onOpen={openFile}
        />
        {notes}
      </div>
    );
  }

  const offlineNote = offline && (
    <p className="mb-4 flex items-center gap-2 text-[14px] text-[#6B7280]">
      <WifiOff size={14} className="flex-shrink-0" />
      {t("deviceTools.firmwareSource.offline")}
    </p>
  );

  const pending = !pkg && loading;
  const none = latest && !latest.production && !latest.dev;

  if (latest === null || pending) {
    // Finding (or downloading) the newest release.
    if (pending) {
      return (
        <div className={className}>
          {offlineNote}
          <VersionSummary
            currentVersion={currentVersion}
            label={releaseLabel(loading)}
            channel={loading.channel}
            busy
          />
        </div>
      );
    }
    return (
      <div className={className}>
        <div className="h-[58px] animate-pulse rounded-xl bg-[#F3F4F6]" />
      </div>
    );
  }

  if (!pkg && none) {
    return (
      <div className={className}>
        {fileInput}
        {offlineNote}
        <div className="rounded-xl bg-[#F9FAFB] px-4 py-5 text-center">
          <p className="text-[14px] text-[#374151]">
            {offline
              ? t("deviceTools.firmwareSource.noneSaved")
              : t("deviceTools.firmwareSource.nonePublishedYet")}
          </p>
          <button
            onClick={openFile}
            className={`${btnSmall} mx-auto mt-3 bg-white`}
          >
            <FileArchive size={14} />
            {t("deviceTools.firmwareSource.chooseFile")}
          </button>
        </div>
      </div>
    );
  }

  if (listOpen) {
    const selectedTag = pkg?.source === "release" ? pkg.sourceTag : null;
    const newest = latest?.[tab];
    let rows =
      lists[tab] === null ? null : mergeReleases(lists[tab], saved, tab);
    if (rows && newest && !rows.some((r) => r.tag === newest.tag)) {
      rows = [newest, ...rows];
    }
    return (
      <div className={className}>
        {fileInput}
        {offlineNote}
        <div className="flex items-center gap-3">
          <p className="flex-1 text-[16px] font-semibold text-[#111827]">
            {t("deviceTools.firmwareSource.chooseTitle")}
          </p>
          {pkg && (
            <button onClick={() => setChoosing(false)} className={btnText}>
              {t("common.actions.cancel")}
            </button>
          )}
        </div>
        <div
          role="tablist"
          className="mt-3 inline-flex rounded-lg bg-[#F3F4F6] p-1"
        >
          {FIRMWARE_CHANNELS.map((channel) => (
            <button
              key={channel}
              role="tab"
              aria-selected={tab === channel}
              onClick={() => setTab(channel)}
              className={`rounded-md px-3 py-1.5 text-[14px] font-medium transition duration-150 ${
                tab === channel
                  ? "bg-white text-[#111827] shadow-sm"
                  : "text-[#6B7280] hover:text-[#374151]"
              }`}
            >
              {t(`deviceTools.firmwareSource.tabs.${channel}`)}
            </button>
          ))}
        </div>
        <div className="mt-3 overflow-hidden rounded-xl border border-[#F3F4F6]">
          {rows === null ? (
            <div className="space-y-px">
              {[0, 1, 2].map((i) => (
                <div key={i} className="h-12 animate-pulse bg-[#F9FAFB]" />
              ))}
            </div>
          ) : rows.length === 0 ? (
            <p className="px-4 py-6 text-center text-[14px] text-[#6B7280]">
              {t("deviceTools.firmwareSource.noneOnChannel")}
            </p>
          ) : (
            <ul className="max-h-80 divide-y divide-[#F3F4F6] overflow-y-auto">
              {rows.map((release) => (
                <ReleaseRow
                  key={release.tag}
                  release={{
                    ...release,
                    saved: saved.some((s) => s.tag === release.tag),
                  }}
                  latest={newest?.tag === release.tag}
                  selected={selectedTag === release.tag}
                  loading={loading?.tag === release.tag}
                  disabled={!!loading}
                  onPick={() => loadRelease(release)}
                />
              ))}
            </ul>
          )}
        </div>
        <button onClick={openFile} className={`${btnText} mt-4`}>
          <FileArchive size={14} />
          {t("deviceTools.firmwareSource.chooseFile")}
        </button>
      </div>
    );
  }

  // `chosen` is gone if this remounted (e.g. after a failed update).
  const savedRow = saved.find((r) => r.tag === pkg.sourceTag);
  const shown =
    pkg.source !== "release"
      ? null
      : chosen?.tag === pkg.sourceTag
      ? chosen
      : savedRow && fromLocal(savedRow);
  return (
    <div className={className}>
      {fileInput}
      {offlineNote}
      {shown ? (
        <VersionSummary
          currentVersion={currentVersion}
          label={releaseLabel(shown)}
          channel={shown.channel}
          meta={
            shown.releasedAt &&
            t("deviceTools.firmwareSource.releasedOn", {
              date: fmtDate(shown.releasedAt),
            })
          }
          onChange={() => setChoosing(true)}
        />
      ) : (
        <FilePicker
          pkg={pkg}
          currentVersion={currentVersion}
          onOpen={() => setChoosing(true)}
        />
      )}
      {notes}
    </div>
  );
};

export default FirmwareSource;
